<script lang="ts">
import {Vue, Component, Prop, Inject} from 'vue-facing-decorator';
import {useRouter} from "vue-router";
import ProjectPreviewModel from "@/models/ProjectPreviewModel";
import { usePostHog } from '@/composables/usePostHog';

@Component({})
export default class ProjectPreview extends Vue {
  @Prop({required: true}) public model!: ProjectPreviewModel;
  @Prop({required: false}) public sizeProp?: number;
  @Prop({required: false}) public canRedirect?: boolean;
  @Prop({default: false}) public isPreview!: boolean;
  public router = useRouter();
  public posthog = usePostHog().posthog;


  public async navigateToProject() {
    if (this.canRedirect && this.model) {
      this.posthog.capture('project_clicked', {
        project_name: this.model.pathToProject,
        is_preview: this.isPreview
      });
      const card = this.$el as HTMLElement;
      card.classList.add('animate');
      await new Promise((resolve) => setTimeout(resolve, 500));
      card.classList.remove('animate');
      await this.router.push({name: this.model.pathToProject});
    }
  }


}
</script>

<template>
  <div class="project-card" v-if="model" @click="navigateToProject">
    <div class="faded-top no-scrollbar">
      <component :is="model.project" :is-preview="isPreview"/>
    </div>
  </div>
</template>

<style scoped>
.project-card {
  width: 100%;
  max-width: 800px;
  flex: 1; /* Allow cards to share height in the column */
  min-height: 0; /* Allow shrinking below content size */
  max-height: 30%; /* Ensure 3 cards fit comfortably */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 24px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  position: relative;
}

.project-card::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: var(--ln-radius-md);
  background: linear-gradient(135deg, var(--ln-glass-border-strong) 0%, transparent 50%, var(--ln-glass-border) 100%);
  opacity: 0;
  transition: opacity var(--ln-transition-normal) ease;
  z-index: -1;
}

.project-card:hover {
  cursor: pointer;
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 16px 32px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-glass-border-strong);
}

.project-card:hover::before {
  opacity: 0.3;
}

.project-card:active {
  transform: translateY(-4px) scale(1.01);
  box-shadow: 0 8px 16px var(--ln-glass-shadow);
}

.faded-top {
  position: relative;
  height: 100%;
  width: 100%;
  pointer-events: none; /* Prevent internal scrolling/interactions */
}

/* Prevents internal scrollbars in preview mode */
.faded-top :deep(*) {
  max-width: 100% !important;
  overflow: hidden !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

.faded-top :deep(*)::-webkit-scrollbar {
  display: none !important;
}

/* Specifically target SVGs and large elements in preview */
.faded-top :deep(svg), 
.faded-top :deep(.svg-container),
.faded-top :deep(.code-content),
.faded-top :deep(img) {
  max-width: 100% !important;
  height: auto !important;
  min-width: 0 !important;
}

.faded-top::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to top,
    var(--ln-bg-gradient-end) 0%,
    var(--ln-glass-surface) 30%,
    transparent 100%
  );
  pointer-events: none;
  opacity: 0.9;
  z-index: 2;
}

.animate {
  animation: growAndFade 0.5s var(--ln-ease-smooth) forwards;
}

@keyframes growAndFade {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateY(-12px) scale(1.08);
    opacity: 0.9;
    box-shadow: 0 20px 40px var(--ln-glass-shadow-elevated);
  }
  100% {
    transform: translateY(-8px) scale(0.95);
    opacity: 0;
  }
}

/* Mobile responsive - Full width cards */
@media (max-width: 768px) {
  .project-card {
    width: 100%; /* Use full width on mobile */
  }
}
</style>
